/**
 * LLM Configuration Page (OpenRouter)
 */
import PageHeader from '@/components/ui/PageHeader';
import UsageChip from '@/components/common/UsageChip';
import StatCard from '@/components/ui/StatCard';
import { useLLMConfig } from '@/hooks/useLLMConfig';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { Add, Block, Check, Delete, Info, Refresh, Save } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card, CardContent,
  Chip,
  CircularProgress, Divider,
  Grid,
  IconButton,
  Slider,
  Tab,
  Table, TableBody, TableCell,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import React, { useState } from 'react';
import { usePlanGate } from '@/hooks/usePlanGate';
import { formatTokensCompact } from '@/utils/formatTokens.util';

const LLMConfig: React.FC = () => {
  const { config, usage, loading, error, success, updateConfig, addModel, deleteModel, refetch, refetchUsage } =
    useLLMConfig();
  const { guard } = usePlanGate();
  const [tab, setTab] = useState(0);
  const [localConfig, setLocalConfig] = useState(config);
  const [newModelId, setNewModelId] = useState('');
  const [addingModel, setAddingModel] = useState(false);
  const [addModelError, setAddModelError] = useState('');

  React.useEffect(() => {
    if (!config) return;
    setLocalConfig(config);
  }, [config]);

  const handleSave = async () => {
    await updateConfig({
      apiKey: localConfig.apiKey,
      temperature: localConfig.temperature,
      maxTokens: localConfig.maxTokens,
    });
  };
  const handleRefresh = () => { refetch(); refetchUsage(); };

  const handleAddModel = async () => {
    const trimmed = newModelId.trim();
    if (!trimmed) return;
    setAddingModel(true);
    setAddModelError('');
    try {
      await addModel(trimmed);
      setNewModelId('');
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setAddModelError(err.message || 'Failed to add model');
    } finally {
      setAddingModel(false);
    }
  };

  if (loading && tab === 0 && !config.apiKey) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px"><CircularProgress /></Box>;
  }

  return (
    <Box>
      <PageHeader
        title="LLM Configuration"
        subtitle="Configure AI models via openrouter.ai"
        actions={<><UsageChip resource="aiTokens" label="AI tokens" format="tokens" /><Button startIcon={<Refresh />} onClick={handleRefresh} disabled={loading} variant="outlined" size="small">Refresh</Button></>}
      />

      {success && <Alert severity="success" icon={<Check />} sx={{ mb: 2 }} onClose={() => {}}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => {}}>{error}</Alert>}

      <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Configuration" /><Tab label="Your Models" /><Tab label="Usage & Costs" />
        </Tabs>
      </Box>

      {/* ── Configuration ── */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>OpenRouter API Key</Typography>
                <TextField
                  fullWidth type="password" label="API Key"
                  value={localConfig.apiKey}
                  onChange={(e) => setLocalConfig({ ...localConfig, apiKey: e.target.value })}
                  placeholder="sk-or-v1-..." helperText="Your OpenRouter API key from openrouter.ai"
                  sx={{ mb: 2 }}
                />
                {!localConfig.apiKey && (
                  <Alert severity="info" icon={<Info />}>
                    Visit openrouter.ai → Settings → API Keys to get your key, then paste it above and save.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Model Parameters</Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom>Temperature: <strong>{localConfig.temperature}</strong></Typography>
                  <Slider value={localConfig.temperature}
                    onChange={(_, v) => setLocalConfig({ ...localConfig, temperature: v as number })}
                    min={0} max={2} step={0.1} valueLabelDisplay="auto"
                    marks={[{ value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }]}
                  />
                  <Typography variant="caption" color="text.secondary">Lower = focused · Higher = creative</Typography>
                </Box>
                <TextField fullWidth type="number" label="Max Tokens" value={localConfig.maxTokens}
                  onChange={(e) => setLocalConfig({ ...localConfig, maxTokens: parseInt(e.target.value) })}
                  helperText="Maximum tokens per response. Used as the default for agents that don't set their own." />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Which model to use is chosen per-agent in Agents → Edit, from the models you add in the "Your Models" tab.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" size="large" startIcon={<Save />} onClick={guard('llmConfigEnabled', handleSave)}
                disabled={loading || !localConfig.apiKey}>
                {loading ? 'Saving…' : 'Save Configuration'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* ── Models ── */}
      {tab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Add a Model</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="OpenRouter model id"
                    placeholder="e.g. openai/gpt-4o-mini"
                    value={newModelId}
                    onChange={(e) => setNewModelId(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') guard('llmConfigEnabled', handleAddModel)(); }}
                    error={Boolean(addModelError)}
                    helperText={addModelError || 'Find model ids at openrouter.ai/models'}
                  />
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={guard('llmConfigEnabled', handleAddModel)}
                    disabled={!newModelId.trim() || addingModel}
                    sx={{ flexShrink: 0 }}
                  >
                    Add
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {config.availableModels?.length > 0 ? (
            config.availableModels.map((model) => {
              const deactivated = model.isEnabled === false;
              return (
                <Grid item xs={12} md={6} key={model.id}>
                  <Card
                    sx={{
                      border: deactivated ? '1px solid #e2e8f0' : '1px solid #e2e8f0',
                      bgcolor: deactivated ? alpha('#94a3b8', 0.06) : 'background.paper',
                      opacity: deactivated ? 0.75 : 1,
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                              {model.id}
                            </Typography>
                            {deactivated && (
                              <Tooltip title={model.deactivatedReason || 'Auto-disabled after a failed call'}>
                                <Chip icon={<Block />} label="Disabled" size="small" color="default" />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {model.provider}
                          </Typography>
                          {deactivated && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Stopped working — delete it and add a different model to recover.
                            </Typography>
                          )}
                        </Box>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={guard('llmConfigEnabled', () => deleteModel(model.id))}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                No models added yet. Add one above, then pick it for an agent in Agents → Edit. Until then, agents use
                the system default model.
              </Alert>
            </Grid>
          )}
        </Grid>
      )}

      {/* ── Usage ── */}
      {tab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}><StatCard title="Tokens Used" value={formatTokensCompact(usage.tokensUsed)} color="#4f46e5" subtitle="Current billing period" /></Grid>
          <Grid item xs={12} sm={4}><StatCard title="Total Cost" value={`$${usage.totalCost.toFixed(2)}`} color="#10b981" subtitle="This month" /></Grid>
          <Grid item xs={12} sm={4}><StatCard title="Requests" value={usage.requestCount.toLocaleString()} color="#f59e0b" subtitle="This month" /></Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Cost Breakdown by Model</Typography>
                <Divider sx={{ mb: 2 }} />
                {usage.modelBreakdown?.length > 0 ? (
                  <Table size="small">
                    <TableBody>
                      {usage.modelBreakdown.map((item: any) => (
                        <TableRow key={item.model}>
                          <TableCell><Typography variant="body2" fontWeight={500}>{item.model}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" color="text.secondary">{formatTokensCompact(item.tokens)} tokens</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2" fontWeight={600} color="primary.main">${item.cost.toFixed(2)}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No usage data yet.</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LLMConfig;
